import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useGetPaginatedConsumersQuery } from "../../../../../redux/api/consumer/consumerAPI";
import { useUpdateRFIDTagMutation } from "../../../../../redux/api/ev-charger/evChargerAPI";
import useDebounce from "../../../../../shared/hooks/useDebounce";
import UpdateOcppTagRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/update-ocpp-tag-request-dto";
import OcppTagResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/ocpp-tag-response-dto";
import { EProviders } from "../../../../../shared/oversight-core/enums/providers";
import { SearchField } from "../../../../../shared/oversight-core/enums/search-field";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import AppButton from "../../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import Pagination from "../../../../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../../../../shared/oversight-core/ui-elements/search/search";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import AddUserModal from "../add-user-modal/add-user-modal";
import styles from "./assign-user-modal.module.scss";

const MIN_AUTO_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

interface IProps extends ModelContainerProps {
  show: boolean;
  tagData?: OcppTagResponseDTO | null;
  onSuccess?: () => void;
  onSelectUser?: (user: { identity: string; name: string }) => void;
}

const AssignUserModal = (props: IProps) => {
  const { show, tagData, onClose, onCancel, onSuccess, onSelectUser, ...rest } =
    props;
  const [searchText, setSearchText] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_MS);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedConsumerId, setSelectedConsumerId] = useState<string | null>(
    null,
  );
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const pageSize = 5;

  const [updateRFIDTag, { isLoading: isUpdating }] = useUpdateRFIDTagMutation();

  const { data: consumersData, isLoading: isLoadingConsumers } =
    useGetPaginatedConsumersQuery({
      pageNumber: currentPage - 1,
      pageSize: pageSize,
      searchField: activeSearch ? SearchField.NAME : SearchField.ALL,
      searchValue: activeSearch,
      serviceProvider: EProviders.ALL,
      tariffCategory: "",
      important: false,
      sortBy: "first_name",
      ascending: true,
    });

  const consumersList = consumersData?.genericPage?.elements || [];

  const handleSearchChange = (val: string) => {
    setSearchText(val);
    if (!val.trim()) {
      setActiveSearch("");
      setCurrentPage(1);
    }
  };

  const applySearch = (text: string) => {
    if (text === activeSearch) return;
    setActiveSearch(text);
    setCurrentPage(1);
    setSelectedConsumerId(null);
  };

  // auto-search once the user has typed enough to narrow the list; shorter
  // queries can still be submitted explicitly with Enter or the search button
  useEffect(() => {
    const trimmed = debouncedSearchText.trim();
    if (trimmed.length >= MIN_AUTO_SEARCH_LENGTH) {
      applySearch(trimmed);
    }
  }, [debouncedSearchText]);

  const handleSearch = () => {
    applySearch(searchText.trim());
  };

  useEffect(() => {
    if (!show) {
      setSearchText("");
      setActiveSearch("");
      setCurrentPage(1);
      setSelectedConsumerId(null);
      setShowAddUserModal(false);
    } else if (tagData && consumersList.length > 0) {
      const matchedConsumer = consumersList.find(
        (c) => c.identity === tagData.consumerId,
      );
      if (matchedConsumer) {
        setSelectedConsumerId(matchedConsumer.identity);
      }
    }
  }, [show, tagData, consumersList]);

  const handleResetAndClose = () => {
    setSearchText("");
    setActiveSearch("");
    setCurrentPage(1);
    setSelectedConsumerId(null);
    setShowAddUserModal(false);
    if (onClose) onClose();
    if (onCancel) onCancel();
  };

  const handleConfirm = () => {
    if (!selectedConsumerId) return;

    const selectedConsumer = consumersList.find(
      (c) => c.identity === selectedConsumerId,
    );
    const fullName = `${selectedConsumer?.personName?.firstName || ""} ${
      selectedConsumer?.personName?.lastName || ""
    }`.trim();

    if (onSelectUser) {
      onSelectUser({
        identity: selectedConsumerId,
        name: fullName,
      });
      handleResetAndClose();
      return;
    }

    if (!tagData?.ocppTagId) return;

    const payload: UpdateOcppTagRequestDTO = {
      ocppTagId: tagData.ocppTagId,
      note: tagData.note || "",
      blocked: tagData.blocked ?? false,
      consumerId: selectedConsumerId,
    };

    updateRFIDTag(payload)
      .unwrap()
      .then(() => {
        showSuccessMessage("User assigned successfully");
        if (onSuccess) onSuccess();
        handleResetAndClose();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err.message || "Failed to assign user");
      });
  };

  const renderUserList = () => {
    if (isLoadingConsumers) {
      return (
        <div className="text-center py-3 text-light">Loading users...</div>
      );
    }

    if (consumersList.length === 0) {
      return (
        <div className="container-dash text-center py-3 font-size-14 text-light">
          No users found
        </div>
      );
    }

    return consumersList.map((consumer) => {
      const fullName =
        `${consumer.personName?.firstName || ""} ${
          consumer.personName?.lastName || ""
        }`.trim() || "-";
      const isSelected = selectedConsumerId === consumer.identity;

      return (
        <div
          key={consumer.identity}
          className={`${styles.userItem} ${
            isSelected ? styles.userItemSelected : ""
          }`}
          onClick={() => setSelectedConsumerId(consumer.identity)}
        >
          <div className={styles.userName}>{fullName}</div>
          <div className={styles.userEmail}>{consumer.email || "-"}</div>
        </div>
      );
    });
  };

  return (
    <>
      <ModalContainer
        show={show}
        {...rest}
        title="Assign User"
        confirmButtonText="Assign"
        cancelButtonText="Cancel"
        onClose={handleResetAndClose}
        onCancel={handleResetAndClose}
        onConfirm={handleConfirm}
        disabled={!selectedConsumerId}
        isLoading={isUpdating}
        size="modal-md"
      >
        <div>
          <Row className="align-items-center">
            <Col>
              <Search
                placeholder="Search User Name"
                value={searchText}
                handleSearchChange={handleSearchChange}
                onSearch={handleSearch}
              />
            </Col>
            <Col xs="auto" className="ps-0">
              <AppButton
                text=""
                type="button"
                iconName="add"
                iconOnly
                onClick={() => setShowAddUserModal(true)}
              />
            </Col>
          </Row>
          <div className={styles.userList}>{renderUserList()}</div>
          <Pagination
            length={consumersData?.genericPage?.totalElements || 0}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            updateCurrentPage={(page) => setCurrentPage(page)}
          />
        </div>
      </ModalContainer>
      <AddUserModal
        show={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onCancel={() => setShowAddUserModal(false)}
      />
    </>
  );
};

export default AssignUserModal;
